/**
 * @license
 * Copyright© Celligence International, LLC - 2023
 * This software program is protected by copyright laws and is the property of
 * Celligence International, LLC (Celligence). Any unauthorized reproduction,
 * distribution, modification, or public display of this program, without the
 * explicit permission of Celligence is strictly prohibited. If you have gained
 * access to this program without the expressed approval of Celligence report
 * the incident to legal@celligence.com.
 */
import React, { lazy, Suspense, useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import "./unwrap.css";
import axios from "axios";
import content from '../content.json';
import parse from 'html-react-parser';
import { useParams } from 'react-router-dom';
import LoanOfficer from "../LoanOfficer/index.js";
import {
  capitalizeFirstLetter,
  formatPhoneNumber,
  addInternationalCode,
} from "../Utils";


export default function WelcomeSection({ dataJson, realtorData }) {
  const imgCDNURL = process.env.REACT_APP_VASTREAMLINE_PROGRAM_CDN_URL;
  const [loInfo, setLoInfo] = useState(null); // Initialize loInfo as null
  const [loanOfficerUid, setLoanOfficerUid] = useState('');
  const { loName } = useParams();
  const { agentName } = useParams();
  const agentJsonName = agentName + ".json";
  const [loading, setLoading] = useState(true);
  const [loadBodyData, setLoadBodyData] = useState(true);
  const [agentData, setAgentData] = useState();
  const [loanOfficerActive, setLoanOfficerActive] = useState(true);
  const [loanOfficerData, setLoanOfficerData] = useState();
  const [realorData, setRealtorData] = useState();
  const [aiBrandingData, setAiBrandingData] = useState();
  const [angelAiUserInfo, setAngelAiUserInfo] = useState();
  const [data, setData] = useState(null);
  const realtorName = realtorData ? capitalizeFirstLetter(realtorData.name) : "";
  const realtorFirstName = realtorData ? capitalizeFirstLetter(realtorData.fisrtName) : "";
  const { whyChooseSunwest, whyChooseSunwesttitle, guidelinedisclaimer, tailoredTitle, tailoredPara, dedicatedTitle, dedicatedPara, dreamTitle, dreamPara, simplifiedTitle, simplifiedPara, loanofficerabout, loanofficeraboutpara } = dataJson;

  let mobilePhoneNumberFormatted,
    mobilePhoneNumberWithInternationalCode,
    mobilePhoneNumberInTitle,
    workPhoneNumberFormatted,
    workPhoneNumberWithInternationalCode,
    workPhoneNumberInTitle;
  try {
    mobilePhoneNumberFormatted = realtorData ? formatPhoneNumber(
      realtorData.mobilePhoneNumber
    ) : "";
    mobilePhoneNumberWithInternationalCode = realtorData ? addInternationalCode(
      realtorData.mobilePhoneNumber,
      "+1"
    ) : "";
    mobilePhoneNumberInTitle = "Contact me at " + mobilePhoneNumberFormatted;
  } catch (error) {
    console.error("Unable to get realtor mobile phone number.", error);
  }

  try {
    workPhoneNumberFormatted = realtorData ? formatPhoneNumber(realtorData.workPhoneNumber) : "";
    workPhoneNumberWithInternationalCode = realtorData ? addInternationalCode(
      realtorData.workPhoneNumber,
      "+1"
    ) : "";
    workPhoneNumberInTitle = "Contact me at " + workPhoneNumberFormatted;
  } catch (error) {
    console.error("Unable to get realtor work phone number.", error);
  }

  useEffect(() => {

    const fetchAiBrandingJson = async () => {
      try {
        await axios
          .get(process.env.REACT_APP_ANGELAI_JSON)
          .then((response) => {
            // console.log(JSON.stringify(response.data));
            setAiBrandingData(response.data);
          })
          .catch((error) => {
            console.log(error);
          });
      } catch (e) {
        console.log(e);
      }
    };

    /**
     * Fetches the Angel AI user info from the specified endpoint.
     *
     * @return {Promise<void>} - A Promise that resolves when the user info has been fetched successfully.
     */
    const fetchAngelAiUserInfo = async () => {
      try {
        await axios
          .get(process.env.REACT_APP_ANGEL_AI_WEB_USER_INFO)
          .then((response) => {
            // console.log(JSON.stringify(response.data));
            setAngelAiUserInfo(response.data);
          })
          .catch((error) => {
            console.log(error);
          });
      } catch (e) {
        console.log(e);
      }
    };

    /**
     * Checks if a loan officer is active.
     *
     * @param {string} loanOfficerId - The ID of the loan officer.
     * @return {Promise<void>} A promise that resolves when the function completes.
     */
    const checkifLoanOfficerIsActive = async (loanOfficerId) => {
      try {
        await axios
          .post(process.env.REACT_APP_CHECK_LO_ACTIVE, {
            loanOfficerId: loanOfficerId,
          })
          .then((response) => {
            // console.log(JSON.stringify(response.data));
            setLoanOfficerActive(response.data.active);
          })
          .catch((error) => {
            console.log(error);
          });
      } catch (e) {
        console.log(e);
      }
    };


    const fetchAgentDetails = async () => {
      try {
        await axios
          .get(
            process.env.REACT_APP_CDN +
            "mlo-product-guidelines/jsons/" +
            agentJsonName
          )
          .then(async (response) => {
            // console.log(JSON.stringify(response.data));
            const responseData = response.data;
            setAgentData(responseData);
            await fetchAiBrandingJson();
            if (responseData) {
              setLoading(false);
              const loanOfficerId = responseData.staff.loanOfficer.ssid;
              await checkifLoanOfficerIsActive(loanOfficerId);
              await fetchAngelAiUserInfo();
              setLoanOfficerData(responseData.staff.loanOfficer);
              setRealtorData(responseData.staff.realtor);
              setLoadBodyData(false);
            }
          })
          .catch((error) => {
            window.location.replace("https://www.swmc.com");
          });
      } catch (e) {
        console.log(e);
      }
    };

    fetchAgentDetails();
  }, []);


  return (
    <>
      <div className="welcome-section">

        <Container>
          <Row>
            <Col xs={12} sm={12} md={12} lg={12} className='why-choose-sunwest-col'>
              <div className="bullet-line">
                <p className='why-choose-sunwest'>{parse(whyChooseSunwest)}</p>
                <img src={imgCDNURL + "spiralimage.webp"} className="spiralimage" alt="spiralimage"
                  loading="lazy"
                  width={""}
                  height={""}></img>
                <p className='why-choose-sunwest-title'>{parse(whyChooseSunwesttitle)}</p>
              </div>
            </Col>
          </Row>
          <Row className="justify-content-between">
            <Col xs={12} sm={12} md={12} lg={3} className='tailored-col'>
              <div className="contentinside">
                <img src={imgCDNURL + "calenderimage.webp"} className='whysunwest-img3' alt="calenderimage"
                  loading="lazy"
                  width={""}
                  height={""}></img>
                <h5 className='whysunwest-title'>{parse(tailoredTitle)}</h5>
                <p className='whysunwest-para'>{parse(tailoredPara)}</p>
              </div>
            </Col>
            <Col xs={12} sm={12} md={12} lg={3} className='dedicated-col tailored-col'>
              <div className="contentinside">
                <img src={imgCDNURL + "handdollarimage.webp"} className='whysunwest-img4' alt="handdollarimage"
                  loading="lazy"
                  width={""}
                  height={""}></img>
                <h5 className='whysunwest-title'>{parse(dedicatedTitle)}</h5>
                <p className='whysunwest-para'>{parse(dedicatedPara)}</p>
              </div>
            </Col>
            <Col xs={12} sm={12} md={12} lg={3} className='dream-col tailored-col'>
              <div className="contentinside">
                <img src={imgCDNURL + "ficoimage.webp"} className='whysunwest-img' alt="ficoimage"
                  loading="lazy"
                  width={""}
                  height={""}></img>
                <h5 className='whysunwest-title'>{parse(dreamTitle)}</h5>
                <p className='whysunwest-para'>{parse(dreamPara)}</p>
              </div>
            </Col>
            <Col xs={12} sm={12} md={12} lg={3} className='dream-col tailored-col'>
              <div className="contentinside">
                <img src={imgCDNURL + "writingimage.webp"} className='whysunwest-img2' alt="writingimage"
                  loading="lazy"
                  width={""}
                  height={""}></img>
                <h5 className='whysunwest-title'>{parse(simplifiedTitle)}</h5>
                <p className='whysunwest-para'>{parse(simplifiedPara)}</p>
              </div>
            </Col>
          </Row>
        </Container>
        <div className="loanofficeraboutcontainer">
          <Container >
            <Row>
              <Col xs={12} sm={12} md={12} lg={12} className="mt-5">
                <div className="loanofficeraboutdiv">
                  <p className='why-choose-sunwest1'>{parse(loanofficerabout)}</p>
                  <p className='why-choose-sunwest2'>{parse(loanofficeraboutpara)}</p>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
        <Container >
          <Row>
            <Col xs={12} sm={12} md={12} lg={12} className='Loanofficer-col'>
              <Suspense fallback={<div />}>
                <LoanOfficer
                  realtorData={realorData}
                  loanOfficerActive={loanOfficerActive}
                  loanOfficerData={loanOfficerData}
                  angelAiUserInfo={angelAiUserInfo}
                />
              </Suspense>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={12} md={12} lg={12} className='guideline-disclaimer-col'>
              <div className="disclaimer-line">

                <p className='guideline-disclaimer'>{parse(guidelinedisclaimer)}</p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    

    </>
  );
}
